import * as formStyles from '../../styles/styles.module.scss';
import * as styles from './styles/styles.module.scss';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import classNames from 'classnames';
import InputAdornment from '@material-ui/core/InputAdornment';
import NoSsr from '@material-ui/core/NoSsr';
import React from 'react';
import TextField from '@material-ui/core/TextField';
import Key from 'types/Key';

const StyledTextField = withStyles({
  root: {
    display: 'flex',
    width: '100%',
    borderRadius: '16px',
    fontFamily: 'Proxima Nova, Arial, sans-serif !important',
    '& .MuiFormLabel-root': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      lineHeight: '8px'
    },
    '& .MuiFormLabel-root.MuiFormLabel-filled': {
      marginTop: '4px'
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 16px',
      margin: '4px 0px',
      borderRadius: '8px',
      color: '#414141',
      fontFamily: 'Proxima Nova, Arial, sans-serif !important'
    },
    '& .MuiInputLabel-outlined': {
      color: '#b2aea7',
      zIndex: 0
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '0px'
    },
    '&.verticalResize .MuiOutlinedInput-input': {
      resize: 'vertical'
    },
    '& input + fieldset': {
      borderRadius: '8px',
      color: '#767676',
      borderColor: '#e6e6e6'
    },
    '& input:valid:focus + fieldset': {
      borderLeftWidth: '2 !important',
      borderWidth: '2',
      borderColor: '#428A13 !important',
      color: '#428A13'
    },
    '& input:focus + fieldset': {
      color: '#428A13',
      borderColor: '#428A13 !important'
    },
    '&. .MuiFormHelperText-contained': {
      margin: '4px 14px'
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: 'red !important'
    },
    '& :hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#428A13 !important'
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      color: '#428A13 !important',
      borderColor: '#428A13 !important'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#428A13',
      marginTop: '4px'
    },
    '& .MuiFormLabel-root.Mui-focused.Mui-error': {
      color: 'red'
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      color: 'red !important'
    },
    '& .MuiOutlinedInput-root.Mui-error': {
      minHeight: '47px !important' // for mobile safari
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      borderColor: 'red !important',
      borderWidth: '2px',
      color: 'red !important'
    }
  }
})(TextField);

function SsrStyledTextField({ label }: { label: React.ReactNode }) {
  return <div className={styles.ssrContainer}>{label}</div>;
}

type Props = {
  adornment?: React.ReactNode;
  autocomplete?: string;
  changeHandler?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  className?: string;
  enterKeyHandler?(event: React.KeyboardEvent<HTMLDivElement> | React.FormEvent): void;
  errorMessage?: React.ReactNode;
  inputMode?: 'numeric';
  isDisabled?: boolean;
  isRequired?: boolean;
  isSuccess?: boolean;
  label?: React.ReactNode;
  maxLength?: number;
  multiline?: boolean;
  name?: string;
  rows?: number;
  verticalResize?: boolean;
  value?: string;
  type?: string;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void;
  onPaste?(event: any): void;
};

function OutlinedTextField({
  adornment,
  autocomplete,
  changeHandler,
  className,
  enterKeyHandler,
  errorMessage,
  isDisabled,
  isRequired,
  isSuccess,
  label,
  name,
  type,
  multiline,
  verticalResize,
  rows,
  value,
  maxLength,
  inputMode,
  onBlur,
  onKeyDown,
  onPaste
}: Props) {
  const onKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === Key.Enter) {
      enterKeyHandler(event);
    }
  };

  function EndAdornment() {
    if (isSuccess) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon className={styles.successCheck} />
        </InputAdornment>
      );
    }

    if (adornment) {
      <InputAdornment position="end">{adornment}</InputAdornment>;
    }

    return null;
  }

  // handle strings and FormattedMessage
  const hasError = errorMessage !== undefined && errorMessage !== '';

  return (
    <NoSsr fallback={<SsrStyledTextField label={label} />}>
      <StyledTextField
        autoComplete={autocomplete}
        className={classNames(
          className,
          styles.container,
          hasError && formStyles.inputError,
          multiline && styles.multiline,
          verticalResize && 'verticalResize'
        )}
        disabled={isDisabled}
        error={hasError}
        fullWidth
        helperText={hasError ? errorMessage : ''}
        InputProps={{
          endAdornment: <EndAdornment />
        }}
        inputProps={{ maxLength, inputMode }}
        label={label}
        multiline={multiline}
        name={name}
        onBlur={onBlur}
        onChange={changeHandler}
        onKeyPress={onKeyPress}
        required={isRequired}
        rows={rows}
        type={type}
        value={value}
        variant="outlined"
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      />
    </NoSsr>
  );
}

export default OutlinedTextField;
